import type { ErrorObject } from "ajv";
import Ajv2020 from "ajv/dist/2020";
import addFormats from "ajv-formats";
import csafSchema from "../schemas/csaf/csaf-2.0.schema.json";
import cvssV2 from "../schemas/csaf/cvss-v2.0.schema.json";
import cvssV30 from "../schemas/csaf/cvss-v3.0.schema.json";
import cvssV31 from "../schemas/csaf/cvss-v3.1.schema.json";

export interface CsafValidationResult {
  valid: boolean;
  errors: string[];
}

type ValidateFunction = ((data: unknown) => boolean) & {
  errors?: ErrorObject[] | null;
};

let compiled: ValidateFunction | null = null;

/**
 * Lazily compile the official OASIS CSAF 2.0 JSON schema (draft 2020-12).
 * The three FIRST.org CVSS schemas referenced by absolute URL are vendored
 * locally and registered by their canonical $id so validation runs fully
 * offline in the browser.
 */
function getValidator(): ValidateFunction {
  if (compiled) {
    return compiled;
  }
  const ajv = new Ajv2020({
    strict: false,
    allErrors: true,
    validateFormats: true,
  });
  addFormats(ajv);
  ajv.addSchema(cvssV2 as object);
  ajv.addSchema(cvssV30 as object);
  ajv.addSchema(cvssV31 as object);
  compiled = ajv.compile(csafSchema as object) as ValidateFunction;
  return compiled;
}

function formatError(error: ErrorObject): string {
  const location =
    error.instancePath && error.instancePath.length > 0
      ? error.instancePath
      : "(document root)";
  const params = (error.params ?? {}) as Record<string, unknown>;

  if (error.keyword === "required" && params.missingProperty) {
    return `${location}: missing required property "${params.missingProperty}"`;
  }
  if (error.keyword === "additionalProperties" && params.additionalProperty) {
    return `${location}: unexpected property "${params.additionalProperty}"`;
  }
  if (error.keyword === "enum" && Array.isArray(params.allowedValues)) {
    return `${location}: ${error.message} (${(params.allowedValues as unknown[]).join(", ")})`;
  }
  return `${location}: ${error.message ?? "is invalid"}`;
}

/**
 * Validate a document against the official OASIS CSAF 2.0 JSON schema.
 * Returns a de-duplicated, human-readable list of errors when invalid.
 */
export function validateCsaf(document: unknown): CsafValidationResult {
  const validate = getValidator();
  const valid = validate(document);
  if (valid) {
    return { valid: true, errors: [] };
  }
  const errors = (validate.errors ?? []).map(formatError);
  return { valid: false, errors: Array.from(new Set(errors)) };
}
